import Link from 'next/link';
import { type FC, type MouseEvent, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Category, type GameV1 } from '@/api/games';
import { ImageComponent } from '@/components/Image';
import { getGameCover } from '@/helpers/cdn';
import {
  amountWithCurrencySymbol,
  DEFAULT_CURRENCIES,
  getCurrencyName,
} from '@/helpers/currency';
import { isFavoriteGame } from '@/helpers/game';
import { DEMO_CURRENCY } from '@/helpers/variables';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import { useCMSGameCovers } from '@/modules/common/hooks/useCMSGameCovers';
import { useTournamentGames } from '@/modules/common/hooks/useTournamentGames';
import { Badge } from '@/modules/game/components/Badge';
import { Badges } from '@/modules/game/components/Badges';
import { HeartButton } from '@/modules/game/components/HeartButton';
import { PlayButton } from '@/modules/game/components/PlayButton';
import type { TCardMode } from '@/modules/game/types';
import { MAP_LOCALE_TO_CURRENCY } from '@/modules/shared';
import { useGetCommonStore } from '@/store';
import { GameCardWrapper } from '../GameCardWrapper';
import { ScGameCardBack, ScGameCardFront, ScGameCardInner } from './styled';

interface IGameCardProps {
  game: GameV1;
  mode: TCardMode;
  withoutBottom?: boolean;
  onPlayClick?: (event: MouseEvent) => void;
  onDemoClick?: (event: MouseEvent) => void;
}

export const GameCard: FC<IGameCardProps> = memo(
  ({ game, mode, withoutBottom, onPlayClick, onDemoClick }) => {
    const { id, real, fullPath, categories: listCollections, seoPath } = game;
    const {
      t,
      i18n: { language },
    } = useTranslation('common');
    const useCommonStore = useGetCommonStore();
    const { tournamentGameCategories, tournamentGameIds } =
      useTournamentGames();

    const favoriteGames = useCommonStore.use.favoriteGames();
    const isAuthenticated = useCommonStore.use.isAuthenticated();

    const isJackpot = useMemo(
      () => listCollections.includes(Category.JACKPOT),
      [listCollections]
    );

    const isFavorite = useMemo(
      () => isFavoriteGame(game.currencies, favoriteGames),
      [game.currencies, favoriteGames]
    );

    const isTournament = useMemo(
      () =>
        tournamentGameCategories.some((item) =>
          listCollections.includes(item)
        ) || tournamentGameIds.includes(seoPath),
      [tournamentGameCategories, tournamentGameIds, seoPath]
    );

    const prize = useMemo(() => {
      const currency =
        MAP_LOCALE_TO_CURRENCY[language as keyof typeof MAP_LOCALE_TO_CURRENCY];
      const jackpot = real[currency]?.jackpot;
      if (!jackpot) return null;
      const { amount } = getCurrencyCentsAndCode(
        jackpot,
        currency,
        DEFAULT_CURRENCIES
      );
      return amountWithCurrencySymbol(getCurrencyName(currency), amount);
    }, [real, language]);

    const { covers } = useCMSGameCovers();

    const isDemoAvailable = useMemo(
      () => !!game.currencies[DEMO_CURRENCY],
      [game.currencies]
    );

    const url = useMemo(
      () => covers?.[id]?.cover?.url || getGameCover(id),
      [covers, id]
    );

    return (
      <GameCardWrapper
        game={game}
        mode={mode}
        onPlayClick={onPlayClick}
        withoutBottom={withoutBottom}
      >
        <ScGameCardInner mode={mode}>
          <ImageComponent
            alt="cover"
            className="cover-image"
            data-testid="game-thumbnail"
            fill
            loading="lazy"
            src={url}
          />
          <ScGameCardBack>
            {isAuthenticated && (
              <HeartButton isFavorite={isFavorite} real={real} />
            )}
            <PlayButton
              data-testid="game-play-button"
              href={`/game/${fullPath}`}
              onClick={onPlayClick}
            />
            {isDemoAvailable && (
              <Link
                className="demo-link"
                data-testid="game-demo-button"
                href={`/game/${fullPath}/?demo=true`}
                onClick={onDemoClick}
              >
                {t('demo')}
              </Link>
            )}
          </ScGameCardBack>
          <ScGameCardFront>
            <Badges
              isCold={game.cold}
              isHot={game.hot}
              isJackpot={isJackpot}
              isTournament={isTournament}
              listCollections={listCollections}
              rtp={game.payout}
            />
            {isFavorite && <Badge className="heart-image" type="heart" />}
            {prize && isJackpot && (
              <div className="tournament-bottom">
                <ImageComponent
                  alt="diamond"
                  className="tournament-image"
                  height={160}
                  src="/jackpot/diamond.webp"
                  width={160}
                />
                <span>{prize}</span>
              </div>
            )}
          </ScGameCardFront>
        </ScGameCardInner>
      </GameCardWrapper>
    );
  }
);
